import {
  Injectable,
  Type,
  InjectionToken,
  AbstractType,
  Injector,
} from '@angular/core';
import {
  merge,
  get,
  isFunction,
  isEmpty,
  map,
  omit,
  head,
  tail,
  concat,
  lowerCase,
} from 'lodash';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { lazyInject } from '@app/containers/base/decorators';
import { CompanyUtilService } from '@app/shared/services/company/company-util.service';
import { StateService, STATE_KEYS } from '@app/shared/services/state-service';
import { TIME_FORMATS } from '@config/global-constants';
import { ToolsActions, UiActions } from '@app/store/actions';
import {
  IPdfDefinitions,
  ICreatedPdf,
  IPdfTools,
  IPdfDefinitionsWithInnerProperty,
  IPdfOptions,
  IPdfBulkOptions,
} from './pdf.type';
import { DEFAULT_PDF_OPTIONS } from './constants';
import { keepSignatureInSamePageBreak } from './page-break-functions';
import {
  IPdfContentBuilder,
  DefaultPdfContentBuilder,
} from './content-builder';
import { IPdfData } from './pdf-data.type';
import { wrapWithLoader } from '../../decorators';
import { popColumnSelector } from './decorators';
import { NumberFormatService } from '@app/shared/services/number-format/number-format.service';
import { isBulkPdf } from './utils/bulk-operate.utils';
import { PdfWorker } from '../pdf-worker';
import { PdfWorkerSelfType } from '../pdf-worker.type';
import { rx } from '@app/shared/rx';
import { batchRequestMap } from '@app/shared/rx-operators/batch-map';
import { fromPromise } from 'rxjs/internal-compatibility';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
class PdfGenerateServiceV2 {
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(CompanyUtilService)
  public companyUtilsService: CompanyUtilService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ToolsActions) public toolActions: ToolsActions;
  @lazyInject(PdfWorker) public pdfWorker: PdfWorker;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public readonly pdfContentBuilder: IPdfContentBuilder;

  public constructor(public injector: Injector) {
    (pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
    this.pdfContentBuilder = new DefaultPdfContentBuilder({
      numberFormatService: this.numberFormatService,
      companyService: this.companyUtilsService,
      uiActions: this.uiActions,
      stateService: this.stateService,
      i18nService: this.i18nService,
    });
  }

  public get serverDataFromState(): string {
    return this.stateService.getByPath(STATE_KEYS.API_INFO)?.serverTimestamp;
  }

  public get serverDate(): string {
    const serverDate = this.stateService.getByPath(
      STATE_KEYS.API_INFO,
    )?.serverTimestamp;

    // #TODO confirm is here need convert to company timezone?
    return !serverDate ? new Date().toISOString() : serverDate;
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public getPdfKitDocument(options: IPdfOptions): PDFKit.PDFDocument {
    return pdfMake
      .createPdf({ ...options, content: { text: 'w' } })
      ?.getStream();
  }

  public getTools(pdfDoc: PDFKit.PDFDocument): IPdfTools {
    return {
      utils: {
        widthOfString: pdfDoc.widthOfString.bind(pdfDoc),
      },
      preference: {
        width: get(pdfDoc, 'page.width'),
        height: get(pdfDoc, 'page.height'),
        size: get(pdfDoc, 'page.size'),
        margins: get(pdfDoc, 'page.margins'),
      },
    };
  }

  public getPdfFileName(
    data: IPdfData,
    pdfDefinition: Partial<IPdfDefinitions>,
  ): string {
    // Handle pdf download name
    if (
      !pdfDefinition.filename &&
      pdfDefinition.title &&
      data.id &&
      !pdfDefinition.options.isBulkExport
    ) {
      pdfDefinition.filename = `${pdfDefinition.title} - ${data.id}`;
    }

    if (!isEmpty(pdfDefinition.filename)) {
      return pdfDefinition.filename;
    }

    if (isFunction(pdfDefinition.generateFileName)) {
      return pdfDefinition.generateFileName(data, pdfDefinition.options);
    }

    return pdfDefinition?.filename;
  }

  public getDefinition(
    customPdfDefinition: Partial<IPdfDefinitions>,
    data: IPdfData,
  ): IPdfDefinitionsWithInnerProperty {
    const pdfOptions =
      this.pdfContentBuilder.getOptionsWithDefault(customPdfDefinition);

    const pdfkitDoc = this.getPdfKitDocument(pdfOptions);

    const pdfDefinition: IPdfDefinitionsWithInnerProperty = {
      options: pdfOptions,
      tools: this.getTools(pdfkitDoc),
    };

    pdfDefinition.filename = this.getPdfFileName(
      data,
      merge({}, pdfDefinition, customPdfDefinition),
    );

    // combine with custom pdf definition
    return merge({}, pdfDefinition, customPdfDefinition);
  }

  /**
   * create pdf
   *
   * @param options pdf options
   * @param data pdf data
   * @param pdfContentBuilder pdf content builder instance
   */
  protected create(
    data: IPdfData,
    definitions?: Partial<IPdfDefinitions>,
    pdfContentBuilder: IPdfContentBuilder = this.pdfContentBuilder,
  ): ICreatedPdf {
    const pdfDefinition = this.getDefinition(definitions, data);

    const pdfOptions = merge(
      {},
      pdfDefinition.options,
      pdfDefinition.options && {
        sortNumber: lowerCase(
          get(
            data,
            get(definitions, 'bulkOptions.sortFiledPath'),
            data.internal_number,
          ),
        ),
      },
    );

    const tools = pdfContentBuilder.getTools(pdfDefinition);

    let pdfHeader = pdfDefinition.header;

    if (definitions?.generateHeader) {
      pdfHeader = definitions.generateHeader(data, tools, pdfOptions);
    }

    const pdfMakeDefinition: TDocumentDefinitions = {
      ...pdfOptions,
      header: pdfHeader,
      content: pdfContentBuilder.buildContent(pdfDefinition, data, {
        removeLastPageBreak: true,
      }),
      pageBreakBefore: get(
        pdfOptions,
        'pageBreakBefore',
        keepSignatureInSamePageBreak,
      ),
    };

    return {
      pdfMakeDefinition,
      filename: pdfDefinition.filename,
      pdfData: data,
    };
  }

  public async createSinglePdf(
    bulkData?: IPdfData,
    definitions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
    omitKeys?: string | string[],
  ): Promise<ICreatedPdf> {
    const mergedOptions = merge({}, DEFAULT_PDF_OPTIONS, definitions?.options);

    if (mergedOptions?.isGetInfoEveryTime) {
      await this.toolActions.fetchInfo();
    }

    const dataFn = get(definitions, 'data');

    let data = dataFn as IPdfData;

    if (isFunction(dataFn)) {
      data = await dataFn(definitions, bulkData);
    }

    const pdf = this.create(data, definitions, pdfContentBuilder);

    return omit(pdf, omitKeys);
  }

  public async bulkCreateAsync(
    pdfBulkOptions: IPdfBulkOptions,
    definitions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
    omitKeysButFirst?: string | string[],
  ): Promise<ICreatedPdf[]> {
    const bulkOptionsData = isFunction(pdfBulkOptions?.data)
      ? await pdfBulkOptions.data()
      : pdfBulkOptions?.data;

    const isBulk = !isEmpty(bulkOptionsData);

    if (!isBulk) {
      const singlePdf = await this.createSinglePdf(
        null,
        definitions,
        pdfContentBuilder,
      );

      return [singlePdf];
    }

    // fetch info first when bulk print
    await this.toolActions.fetchInfo();

    const pdfDefinition = merge({}, definitions, <IPdfDefinitions>{
      options: {
        // not need to get info when bulk print
        isGetInfoEveryTime: false,
      },
    });

    const firstPdf = await this.createSinglePdf(
      head(bulkOptionsData),
      pdfDefinition,
      pdfContentBuilder,
    );

    const batchCount = get(pdfBulkOptions, 'batchCount', 150);

    const bulkPdfList = await batchRequestMap(
      tail(bulkOptionsData),
      (curPdfData) => {
        return fromPromise(
          this.createSinglePdf(
            curPdfData,
            pdfDefinition,
            pdfContentBuilder,
            omitKeysButFirst,
          ),
        );
      },
      batchCount,
    )
      .pipe(
        rx.reduce((totalList, curPdf) => concat(totalList, curPdf), [firstPdf]),
      )
      .toPromise();

    return bulkPdfList;
  }

  /**
   * create pdf async
   * will request tool fetchInfo when !isNotGetInfoEveryTime is true
   *
   * @param options options pdf options
   * @param data pdf data
   * @param pdfContentBuilder pdf content builder instance
   */
  @popColumnSelector()
  @wrapWithLoader('options.isShownLoadingWhenGenerate', 0, true)
  public async createAsync(
    definitions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
    omitKeys?: string | string[],
  ): Promise<ICreatedPdf> {
    return this.createSinglePdf(null, definitions, pdfContentBuilder, omitKeys);
  }

  public async downloadPdfAsync(
    customPdfOptions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
  ): Promise<void> {
    if (isBulkPdf(customPdfOptions)) {
      await this.bulkDownloadPdfAsync(
        customPdfOptions?.bulkOptions,
        customPdfOptions,
        pdfContentBuilder,
      );
    } else {
      const pdf = await this.createAsync(customPdfOptions, pdfContentBuilder);
      if (!pdf) {
        return;
      }
      const globalOptions = this.generateGlobalOptions(
        pdf.pdfMakeDefinition['date'],
      );
      await this.handleExportPdf(pdf, globalOptions);
    }
  }

  @wrapWithLoader('options.isShownLoadingWhenGenerate', 0, true)
  public async handleExportPdf(
    pdf: ICreatedPdf,
    globalOptions: PdfWorkerSelfType,
  ): Promise<void> {
    await this.pdfWorker.exportPdf(
      pdf.pdfMakeDefinition,
      globalOptions,
      pdf.filename?.toString(),
    );
  }

  public async openPdfAsync(
    customPdfOptions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
  ): Promise<void> {
    const pdf = await this.createAsync(customPdfOptions, pdfContentBuilder);
    if (!pdf) {
      return;
    }
    const globalOptions = this.generateGlobalOptions(
      pdf.pdfMakeDefinition['date'],
    );
    await this.pdfWorker.openPdf(pdf.pdfMakeDefinition, globalOptions);
  }

  public async printPdfAsync(
    customPdfOptions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
  ): Promise<void> {
    if (isBulkPdf(customPdfOptions)) {
      await this.bulkPrintPdfAsync(
        customPdfOptions?.bulkOptions,
        customPdfOptions,
        pdfContentBuilder,
      );
    } else {
      const pdf = await this.createAsync(customPdfOptions, pdfContentBuilder);
      if (!pdf) {
        return;
      }
      const globalOptions = this.generateGlobalOptions(
        pdf.pdfMakeDefinition['date'],
      );
      this.handleGlobalOptionsGeneration(pdf, globalOptions);
    }
  }

  @wrapWithLoader('options.isShownLoadingWhenGenerate', 0, true)
  public async handleGlobalOptionsGeneration(
    pdf: ICreatedPdf,
    globalOptions: PdfWorkerSelfType,
  ): Promise<void> {
    await this.pdfWorker.printPdf(pdf.pdfMakeDefinition, globalOptions);
  }

  @popColumnSelector(1)
  @wrapWithLoader('options.isShownLoadingWhenGenerate', 1, true)
  public async bulkPrintPdfAsync(
    bulkData: IPdfBulkOptions,
    definitions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
  ): Promise<void> {
    const pdfList = await this.bulkCreateAsync(
      bulkData,
      definitions,
      pdfContentBuilder,
      'pdfData',
    );
    const globalOptions = this.generateGlobalOptions(
      pdfList[0].pdfMakeDefinition['date'],
    );
    const docDefinitionList = map(pdfList, 'pdfMakeDefinition');

    await this.pdfWorker.bulkPrintPdf(docDefinitionList, globalOptions);
  }

  @popColumnSelector(1)
  @wrapWithLoader('options.isShownLoadingWhenGenerate', 1, true)
  public async bulkDownloadPdfAsync(
    bulkOptions: IPdfBulkOptions,
    definitions?: IPdfDefinitions,
    pdfContentBuilder?: IPdfContentBuilder,
  ): Promise<void> {
    const pdfList = await this.bulkCreateAsync(
      bulkOptions,
      definitions,
      pdfContentBuilder,
      'pdfData',
    );
    const pdfDataList = map(pdfList, 'pdfData');
    const globalOptions = this.generateGlobalOptions(
      pdfList[0].pdfMakeDefinition['date'],
    );
    const docDefinitionList = map(pdfList, 'pdfMakeDefinition');

    const exportFileName = isFunction(bulkOptions?.exportFileName)
      ? bulkOptions.exportFileName(pdfDataList, definitions)
      : bulkOptions.exportFileName;

    await this.pdfWorker.bulkExportPdf(
      docDefinitionList,
      globalOptions,
      exportFileName,
    );
  }

  /**
   * Generate global options
   * Need set it in onmessageHandle of pdf-worker.ts
   *
   * @returns object
   * @example
   * self['createDate'] = globalOptions.createDate;
   */
  private generateGlobalOptions(date: string): PdfWorkerSelfType {
    const printDate = this.companyUtilsService.convertUTCDateWithTimeZone(
      this.serverDate,
      true,
      TIME_FORMATS.L_LT,
    );

    return {
      createDate: date ?? printDate,
      serverDate: this.serverDate,
      allRightsText: this.i18nService.instant(I18n.InfoMessages.AllRightsClaim),
      footerText: this.i18nService.instant(I18n.InfoMessages.ExportFooterText),
      i18n: {
        Page: this.i18nService.instant(I18n.Common.Page),
        of: this.i18nService.instant(I18n.Common.of),
      },
    };
  }
}

export { PdfGenerateServiceV2 };
